// ** Modules
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

// ** Components
import GraphView from './GraphView';
import TableView from './TableView';
import defaultData from './fakeData.json';

// ** Icons
import { GraphIcon, TableIcon } from './icons';

// ** Types
import type { GraphTableData, GraphTableSwitchProps } from './types';

export type { GraphTableData, GraphTableSwitchProps } from './types';
export type { GraphTableSeries, GraphTableSeriesStyle } from './types';

// direction: +1 = graph→table (slides left), -1 = table→graph (slides right)
const MOTION_VARIANTS = {
	enter: (dir: number) => ({ opacity: 0, x: dir * 28 }),
	center: { opacity: 1, x: 0 },
	exit: (dir: number) => ({ opacity: 0, x: dir * -28 })
};
const MOTION_TRANSITION = { duration: 0.26, ease: [0.4, 0, 0.2, 1] } as const;

export default function GraphTableSwitch({
	data = defaultData as GraphTableData,
	initialView = 'graph',
	lineStyleMode = 'mixed'
}: GraphTableSwitchProps) {
	const [view, setView] = React.useState<'graph' | 'table'>(initialView);
	const directionRef = React.useRef(0);

	const handleSetView = React.useCallback((v: 'graph' | 'table') => {
		directionRef.current = v === 'table' ? 1 : -1;
		setView(v);
	}, []);

	return (
		<Box
			sx={{
				width: '100%',
				pt: { xs: 8, sm: 12 },
				pb: { xs: 8, sm: 12 },
				overflow: 'hidden'
			}}
		>
			{/* <Box sx={{ px: { xs: 4, sm: 8, lg: 20 } }}> */}
			{/* Header: title + view toggle */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
					gap: 2
				}}
			>
				<Typography variant="title-md" sx={{ color: 'primary.main' }}>
					{data.title}
				</Typography>

				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					{/* Desktop: animated underline text buttons */}
					<Box
						sx={{
							display: { xs: 'none', sm: 'flex' },
							alignItems: 'center',
							gap: 3
						}}
					>
						{(['graph', 'table'] as const).map((v) => (
							<ButtonBase
								key={v}
								onClick={() => handleSetView(v)}
								disableRipple
								sx={{
									typography: 'body-md',
									lineHeight: '28px',
									color: 'common.black',
									position: 'relative',
									pb: 0.25,
									transition: 'opacity 0.18s ease',
									'&::after': {
										content: '""',
										position: 'absolute',
										bottom: 0,
										left: 0,
										width: '100%',
										height: '1px',
										bgcolor: 'currentColor',
										transformOrigin: 'left center',
										transform: view === v ? 'scaleX(1)' : 'scaleX(0)',
										transition: 'transform 0.2s ease'
									},
									'&:hover::after': { transform: 'scaleX(1)' },
									'&:hover': { opacity: 0.65 }
								}}
							>
								{v === 'graph' ? 'Graphenansicht' : 'Listenansicht'}
							</ButtonBase>
						))}
					</Box>

					{/* Mobile: icon buttons */}
					<Box
						sx={{
							display: { xs: 'flex', sm: 'none' },
							alignItems: 'center',
							gap: 4
						}}
					>
						<IconButton aria-label="Show graph" onClick={() => handleSetView('graph')} disableRipple sx={{ p: 0.5 }}>
							<GraphIcon active={view === 'graph'} />
						</IconButton>
						<IconButton aria-label="Show table" onClick={() => handleSetView('table')} disableRipple sx={{ p: 0.5 }}>
							<TableIcon active={view === 'table'} />
						</IconButton>
					</Box>
				</Box>
			</Box>

			{/* Animated view area */}
			<Box sx={{ position: 'relative' }}>
				<AnimatePresence mode="wait" initial={false} custom={directionRef.current}>
					{view === 'graph' ? (
						<motion.div
							key="graph"
							custom={directionRef.current}
							variants={MOTION_VARIANTS}
							initial="enter"
							animate="center"
							exit="exit"
							transition={MOTION_TRANSITION}
						>
							<Box sx={{ mt: { xs: 6, md: 12 } }}>
								<GraphView data={data} lineStyleMode={lineStyleMode} />
							</Box>
						</motion.div>
					) : (
						<motion.div
							key="table"
							custom={directionRef.current}
							variants={MOTION_VARIANTS}
							initial="enter"
							animate="center"
							exit="exit"
							transition={MOTION_TRANSITION}
						>
							<Box sx={{ mt: { xs: 6, md: 16 } }}>
								<TableView data={data} />
							</Box>
						</motion.div>
					)}
				</AnimatePresence>
			</Box>
			{/* </Box> */}
		</Box>
	);
}
